import React, { useState, useMemo, useCallback } from 'react'
import { Button } from '../../school_panel/components/ui'
import { Brain, Check, ChevronDown, ChevronUp, FolderInput, Library, RotateCw, Search, TriangleAlert, X } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { PipelineQuestion, PipelineResults } from '../../hooks/usePipelineEngine'
import CollapsedQuestionCard from './CollapsedQuestionCard'

interface Props {
    questions: PipelineQuestion[]
    results: PipelineResults
    onRetryFailed: () => void
    onRetryNoContextAllBooks: (indices: number[]) => void
    onUseAIKnowledge: (indices: number[]) => void
    onSearchSpecificBooks?: (index: number) => void
    onSearchSpecificBooksBulk?: (indices: number[]) => void
    onReassignChapter?: (index: number) => void
    onReassignChapterBulk?: (indices: number[]) => void
    /** Manual edits after processing (chapter list + setters). */
    catalog?: any[]
    onSetChapter?: (index: number, entry: any) => void
    onEditMeta?: (index: number, patch: { explanation?: string; references?: string[] }) => void
    onSave: () => void
    isSaving: boolean
}

type BulkAction = 'searchAll' | 'searchSpecific' | 'aiKnowledge' | 'reassign'

const bulkActionLabels: Record<BulkAction, { label: string; icon: LucideIcon }> = {
    searchAll: { label: 'Search all books', icon: Search },
    searchSpecific: { label: 'Search specific books', icon: Library },
    aiKnowledge: { label: 'Use AI knowledge', icon: Brain },
    reassign: { label: 'Reassign chapter', icon: FolderInput },
}

const SECTION_HEAD = 'flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent'
const CHEVRON = { size: 18, strokeWidth: 1.8, 'aria-hidden': true, className: 'flex-none text-muted' } as const
const ROW_CHECK = 'mt-3.5 h-4 w-4 flex-none accent-navy'

const PipelineSummary: React.FC<Props> = ({
    questions,
    results,
    onRetryFailed,
    onRetryNoContextAllBooks,
    onUseAIKnowledge,
    onSearchSpecificBooks,
    onSearchSpecificBooksBulk,
    onReassignChapter,
    onReassignChapterBulk,
    onSave,
    isSaving,
    catalog,
    onSetChapter,
    onEditMeta,
}) => {
    const [expandSucceeded, setExpandSucceeded] = useState(false)
    const [expandFailed, setExpandFailed] = useState(false)
    const [expandNoContext, setExpandNoContext] = useState(false)

    // Bulk action state
    const [selectionMode, setSelectionMode] = useState<'failed' | 'noContext' | null>(null)
    const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set())
    const [pendingAction, setPendingAction] = useState<{ action: BulkAction; section: 'failed' | 'noContext' } | null>(null)
    const [showChoiceDialog, setShowChoiceDialog] = useState(false)

    // Derive sections from actual question statuses
    const liveSucceeded = useMemo(() => questions.map((q, i) => ({ q, i })).filter(({ q }) => q._status === 'explanation_done').map(({ i }) => i), [questions])
    const liveFailed = useMemo(() => questions.map((q, i) => ({ q, i })).filter(({ q }) => q._status === 'failed').map(({ i }) => i), [questions])
    const liveNoContext = useMemo(() => questions.map((q, i) => ({ q, i })).filter(({ q }) => q._status === 'no_context').map(({ i }) => i), [questions])

    const succeededCount = liveSucceeded.length
    const failedCount = liveFailed.length
    const noContextCount = liveNoContext.length

    const handleSearchAllBooks = (idx: number) => onRetryNoContextAllBooks([idx])
    const handleUseAIKnowledge = (idx: number) => onUseAIKnowledge([idx])

    // Bulk action flow
    const handleBulkAction = useCallback((action: BulkAction, section: 'failed' | 'noContext') => {
        setPendingAction({ action, section })
        setShowChoiceDialog(true)
    }, [])

    const handleChoiceAll = useCallback(() => {
        if (!pendingAction) return
        const indices = pendingAction.section === 'failed' ? liveFailed : liveNoContext
        executeBulkAction(pendingAction.action, indices)
        setShowChoiceDialog(false)
        setPendingAction(null)
    }, [pendingAction, liveFailed, liveNoContext])

    const handleChoiceSelected = useCallback(() => {
        if (!pendingAction) return
        setShowChoiceDialog(false)
        setSelectionMode(pendingAction.section)
        setSelectedIndices(new Set())
        // Expand the section
        if (pendingAction.section === 'failed') setExpandFailed(true)
        else setExpandNoContext(true)
    }, [pendingAction])

    const handleCancelSelection = useCallback(() => {
        setSelectionMode(null)
        setSelectedIndices(new Set())
        setPendingAction(null)
    }, [])

    const handleProcessSelected = useCallback(() => {
        if (!pendingAction || selectedIndices.size === 0) return
        executeBulkAction(pendingAction.action, Array.from(selectedIndices))
        setSelectionMode(null)
        setSelectedIndices(new Set())
        setPendingAction(null)
    }, [pendingAction, selectedIndices])

    const executeBulkAction = useCallback((action: BulkAction, indices: number[]) => {
        if (indices.length === 0) return
        switch (action) {
            case 'searchAll':
                onRetryNoContextAllBooks(indices)
                break
            case 'searchSpecific':
                if (onSearchSpecificBooksBulk) onSearchSpecificBooksBulk(indices)
                else if (onSearchSpecificBooks) indices.forEach(i => onSearchSpecificBooks(i))
                break
            case 'aiKnowledge':
                onUseAIKnowledge(indices)
                break
            case 'reassign':
                if (onReassignChapterBulk) onReassignChapterBulk(indices)
                else if (onReassignChapter) indices.forEach(i => onReassignChapter(i))
                break
        }
    }, [onRetryNoContextAllBooks, onSearchSpecificBooks, onSearchSpecificBooksBulk, onUseAIKnowledge, onReassignChapter, onReassignChapterBulk])

    const toggleIndex = useCallback((idx: number) => {
        setSelectedIndices(prev => {
            const next = new Set(prev)
            if (next.has(idx)) next.delete(idx)
            else next.add(idx)
            return next
        })
    }, [])

    const toggleAll = useCallback((indices: number[]) => {
        setSelectedIndices(prev => {
            const allSelected = indices.every(i => prev.has(i))
            if (allSelected) return new Set()
            return new Set(indices)
        })
    }, [])

    const BulkActionButtons: React.FC<{ section: 'failed' | 'noContext' }> = ({ section }) => {
        const isInSelectionMode = selectionMode === section
        const sectionIndices = section === 'failed' ? liveFailed : liveNoContext

        if (isInSelectionMode) {
            return (
                <div className="flex flex-wrap items-center gap-2 border-b border-line bg-mist px-4 py-2">
                    <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-ink">
                        <input
                            type="checkbox"
                            checked={sectionIndices.length > 0 && sectionIndices.every(i => selectedIndices.has(i))}
                            onChange={() => toggleAll(sectionIndices)}
                            className="h-4 w-4 accent-navy"
                        />
                        Select all
                    </label>
                    <span className="font-mono text-xs text-muted">({selectedIndices.size} selected)</span>
                    <div className="flex-1" />
                    <Button size="sm" disabled={selectedIndices.size === 0} onClick={handleProcessSelected}>
                        Process {selectedIndices.size} selected
                    </Button>
                    <Button size="sm" variant="ghost" onClick={handleCancelSelection}>
                        Cancel
                    </Button>
                </div>
            )
        }

        return (
            <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">
                {Object.entries(bulkActionLabels).map(([key, { label, icon: Icon }]) => (
                    <button
                        key={key}
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleBulkAction(key as BulkAction, section) }}
                        className="inline-flex min-h-[34px] items-center gap-1.5 rounded-[10px] border border-line-strong bg-surface px-[11px] text-xs font-semibold text-ink transition-colors hover:border-slate focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    >
                        <Icon size={14} strokeWidth={1.8} aria-hidden /> {label}
                    </button>
                ))}
            </div>
        )
    }

    return (
        <div className="space-y-[18px]">
            {/* Summary tiles */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {([
                    ['Succeeded', succeededCount, Check, 'bg-success-bg text-success'],
                    ['Failed', failedCount, X, 'bg-danger-bg text-danger-ink'],
                    ['No context', noContextCount, TriangleAlert, 'bg-warning-bg text-warning-ink'],
                ] as const).map(([label, count, Icon, tone]) => (
                    <div key={label} className="flex items-center gap-3.5 rounded-[18px] border border-line bg-surface p-4 shadow-card">
                        <span aria-hidden className={`grid h-11 w-11 flex-none place-items-center rounded-[14px] ${tone}`}><Icon size={18} strokeWidth={1.8} /></span>
                        <div className="min-w-0">
                            <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">{label}</div>
                            <div className="font-mono text-[28px] font-semibold leading-none tracking-[-0.04em] text-ink">{count}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Succeeded section */}
            {succeededCount > 0 && (
                <div className="overflow-hidden rounded-[18px] border border-line bg-surface">
                    <button
                        type="button"
                        onClick={() => setExpandSucceeded(!expandSucceeded)}
                        aria-expanded={expandSucceeded}
                        className={SECTION_HEAD}
                    >
                        <span className="flex items-center gap-2 text-sm font-semibold text-success">
                            <Check size={16} strokeWidth={2} aria-hidden /> Succeeded <span className="font-mono">({succeededCount})</span>
                        </span>
                        {expandSucceeded ? <ChevronUp {...CHEVRON} /> : <ChevronDown {...CHEVRON} />}
                    </button>
                    {expandSucceeded && (
                        <div className="max-h-[400px] space-y-2 overflow-y-auto border-t border-hairline p-3">
                            {liveSucceeded.map(idx => (
                                <CollapsedQuestionCard
                                    key={idx}
                                    q={questions[idx]}
                                    index={idx}
                                    onSearchAllBooks={handleSearchAllBooks}
                                    onSearchSpecificBooks={onSearchSpecificBooks}
                                    onUseAIKnowledge={handleUseAIKnowledge}
                                    onReassignChapter={onReassignChapter}
                                    catalog={catalog}
                                    onSetChapter={onSetChapter}
                                    onEditMeta={onEditMeta}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Failed section */}
            {failedCount > 0 && (
                <div className="overflow-hidden rounded-[18px] border border-danger/30 bg-surface">
                    <div className="flex items-center bg-danger-bg">
                        <button
                            type="button"
                            onClick={() => setExpandFailed(!expandFailed)}
                            aria-expanded={expandFailed}
                            className={`${SECTION_HEAD} min-w-0 flex-1 hover:bg-transparent`}
                        >
                            <span className="flex items-center gap-2 text-sm font-semibold text-danger-ink">
                                <X size={16} strokeWidth={2} aria-hidden /> Failed <span className="font-mono">({failedCount})</span>
                            </span>
                            {expandFailed ? <ChevronUp {...CHEVRON} /> : <ChevronDown {...CHEVRON} />}
                        </button>
                        <div className="flex-none pr-3">
                            <Button size="sm" variant="danger" icon={<RotateCw size={14} strokeWidth={1.8} aria-hidden />} onClick={(e: React.MouseEvent) => { e.stopPropagation(); onRetryFailed() }}>
                                Retry {failedCount} failed
                            </Button>
                        </div>
                    </div>
                    <BulkActionButtons section="failed" />
                    {expandFailed && (
                        <div className="max-h-[400px] space-y-2 overflow-y-auto p-3">
                            {liveFailed.map(idx => (
                                <div key={idx} className="flex items-start gap-2">
                                    {selectionMode === 'failed' && (
                                        <input
                                            type="checkbox"
                                            checked={selectedIndices.has(idx)}
                                            onChange={() => toggleIndex(idx)}
                                            aria-label={`Select question ${idx + 1}`}
                                            className={ROW_CHECK}
                                        />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <CollapsedQuestionCard
                                            q={questions[idx]}
                                            index={idx}
                                            onSearchAllBooks={handleSearchAllBooks}
                                            onSearchSpecificBooks={onSearchSpecificBooks}
                                            onUseAIKnowledge={handleUseAIKnowledge}
                                            onReassignChapter={onReassignChapter}
                                            catalog={catalog}
                                            onSetChapter={onSetChapter}
                                            onEditMeta={onEditMeta}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* No Context section */}
            {noContextCount > 0 && (
                <div className="overflow-hidden rounded-[18px] border border-line bg-surface">
                    <button
                        type="button"
                        onClick={() => setExpandNoContext(!expandNoContext)}
                        aria-expanded={expandNoContext}
                        className={`${SECTION_HEAD} bg-warning-bg hover:bg-warning-bg`}
                    >
                        <span className="flex items-center gap-2 text-sm font-semibold text-warning-ink">
                            <TriangleAlert size={16} strokeWidth={1.8} aria-hidden /> Needs attention <span className="font-mono">({noContextCount})</span>
                        </span>
                        {expandNoContext ? <ChevronUp {...CHEVRON} /> : <ChevronDown {...CHEVRON} />}
                    </button>
                    <BulkActionButtons section="noContext" />
                    {expandNoContext && (
                        <div className="max-h-[400px] space-y-2 overflow-y-auto p-3">
                            {liveNoContext.map(idx => (
                                <div key={idx} className="flex items-start gap-2">
                                    {selectionMode === 'noContext' && (
                                        <input
                                            type="checkbox"
                                            checked={selectedIndices.has(idx)}
                                            onChange={() => toggleIndex(idx)}
                                            aria-label={`Select question ${idx + 1}`}
                                            className={ROW_CHECK}
                                        />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <CollapsedQuestionCard
                                            q={questions[idx]}
                                            index={idx}
                                            onSearchAllBooks={handleSearchAllBooks}
                                            onSearchSpecificBooks={onSearchSpecificBooks}
                                            onUseAIKnowledge={handleUseAIKnowledge}
                                            onReassignChapter={onReassignChapter}
                                            catalog={catalog}
                                            onSetChapter={onSetChapter}
                                            onEditMeta={onEditMeta}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* All or Selected choice dialog */}
            {showChoiceDialog && pendingAction && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-navy-deep/60 p-4" onClick={() => { setShowChoiceDialog(false); setPendingAction(null) }}>
                    <div role="dialog" aria-modal="true" aria-labelledby="bulk-choice-title" className="max-h-[90dvh] w-full max-w-sm space-y-4 overflow-y-auto rounded-[20px] border border-line bg-surface p-6 shadow-card" onClick={e => e.stopPropagation()}>
                        <h3 id="bulk-choice-title" className="flex items-center justify-center gap-2 text-center font-display text-[17px] font-bold text-ink">
                            {(() => { const Icon = bulkActionLabels[pendingAction.action].icon; return <Icon size={18} strokeWidth={1.8} aria-hidden /> })()} {bulkActionLabels[pendingAction.action].label}
                        </h3>
                        <p className="text-center text-sm text-muted">
                            Apply to all {pendingAction.section === 'failed' ? failedCount : noContextCount} questions or select specific ones?
                        </p>
                        <div className="flex gap-3">
                            <Button className="flex-1 justify-center" onClick={handleChoiceAll}>
                                All questions
                            </Button>
                            <Button className="flex-1 justify-center" variant="ghost" onClick={handleChoiceSelected}>
                                Select questions
                            </Button>
                        </div>
                        <button
                            type="button"
                            onClick={() => { setShowChoiceDialog(false); setPendingAction(null) }}
                            className="w-full rounded-[10px] py-2 text-center text-xs font-semibold text-muted hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Save button */}
            <div className="flex flex-wrap items-center justify-end gap-2 rounded-[18px] border border-line bg-surface px-[18px] py-3.5 shadow-card">
                {succeededCount === 0 && noContextCount === 0 && (
                    <span className="mr-auto text-[13px] text-muted">No questions are ready to save yet. Retry the failed ones first.</span>
                )}
                <Button
                    onClick={onSave}
                    loading={isSaving}
                    disabled={isSaving || (succeededCount === 0 && noContextCount === 0)}
                >
                    {isSaving ? 'Saving…' : `Save ${succeededCount + noContextCount} ${succeededCount + noContextCount === 1 ? 'question' : 'questions'}`}
                </Button>
            </div>
        </div>
    )
}

export default PipelineSummary
